import { useOutletContext } from "react-router-dom";
import { Locale } from "@dealer-center/ddc-core-utils";
import { useCC9Flags } from "@dealer-center/core-third-party-launch-darkly";
import type { ControlcenterAPIInterfaceExternal } from "@dealer-center/ddc-pages-controlcenter-interface";
import type { decodedJwtAndPermissions } from "@dealer-center/ddc-api-user-interface";
import { InventoryManagerProvider } from "./providers/InventoryManagerProvider";
import { Banner } from "./components/Banner";
import { InventoryManager } from "./InventoryManager";
import LABELS from "./labels.json";

/**
 * Page shell for Inventory Manager. Pulls ccUserInfo from the control-center
 * outlet, installs labels, and gates access client-side only — on the
 * `inventory-manager-page-access` LD flag plus the INVENTORY.MANAGER user
 * permission. Per-feature gating on the ~45 getPermissionFlags values is a live
 * network call and is deferred to the BFF-hookup follow-up story, so this shell
 * does not call getPermissionFlags. On success it mounts the context
 * provider(s) around the existing InventoryManager page.
 */
export const InventoryManagerWrapper = () => {
  const { ccUserInfo } = useOutletContext<{
    ccUserInfo: ControlcenterAPIInterfaceExternal;
    userEntity: decodedJwtAndPermissions;
  }>();
  const { "inventory-manager-page-access": shouldDisplayInventory } =
    useCC9Flags();

  Locale.setLabelsOnce(LABELS);

  // Treat an as-yet-unresolved flag (undefined while LaunchDarkly initializes)
  // as "not blocked" — mirror InventoryManager/IncentivesManager, which gate
  // only on an explicit `false`, to avoid a no-access flash (or a hard lockout
  // if LD never resolves) for a permitted user.
  const hasAccess =
    shouldDisplayInventory !== false &&
    ccUserInfo.userPermissions.includes("INVENTORY.MANAGER");

  if (!hasAccess) {
    return (
      <Banner variant="error">{Locale.getText("NO_ACCESS") as string}</Banner>
    );
  }

  return (
    <InventoryManagerProvider>
      <InventoryManager />
    </InventoryManagerProvider>
  );
};
